import { useCallback } from 'react';
import { SimulationConfig, Ball, Vector2D, ShapeType, EscapeRing, TrailCircle, CollisionEvent, BlastEvent, SpaceSpiralRoundConfig, BallPiece, BallPlacementConfig, RhythmicSimulationState } from '../types';
import { TrailCirclePool } from '../services/TrailCirclePool';
import { AUDIO } from '../services/AudioService';
import { CAMERA } from '../services/CameraService';
import { getRandomColor } from '../lib/colorUtils';
import { BallPool } from '../services/BallPool';
import { EscapeLogic } from '../logic/modes/EscapeLogic';
import { SpaceSpiralLogic } from '../logic/modes/SpaceSpiralLogic';
import { RhythmicLogic } from '../logic/modes/RhythmicLogic';
import { RhythmicEscapeLogic } from '../logic/modes/RhythmicEscapeLogic';
import { BasicModesLogic } from '../logic/modes/BasicModesLogic';
import { PRESET_LIBRARY } from '../presets';

export const useSimulationInit = (
  configRef: React.MutableRefObject<SimulationConfig>,
  ballsRef: React.MutableRefObject<Ball[]>,
  trailCirclesRef: React.MutableRefObject<TrailCircle[]>,
  collisionEventsRef: React.MutableRefObject<CollisionEvent[]>,
  blastEventsRef: React.MutableRefObject<BlastEvent[]>,
  escapeRingsRef: React.MutableRefObject<EscapeRing[]>,
  initialEscapeRingCountRef: React.MutableRefObject<number>,
  timeScaleRef: React.MutableRefObject<number>,
  isRotationStoppedRef: React.MutableRefObject<boolean>,
  shakeRef: React.MutableRefObject<number>,
  counterPopRef: React.MutableRefObject<number>,
  timeRemainingRef: React.MutableRefObject<number>,
  containerRadiusRef: React.MutableRefObject<number>,
  rhythmicIndexRef: React.MutableRefObject<number>,
  rhythmicTimeScaleRef: React.MutableRefObject<number>,
  rhythmicTimeScaleRateRef: React.MutableRefObject<number>,
  morphTimerRef: React.MutableRefObject<number>,
  morphStateRef: React.MutableRefObject<{ active: boolean, from: ShapeType, to: ShapeType, progress: number }>,
  escapeAccelerationBoostRef: React.MutableRefObject<number>,
  currentRoundRef: React.MutableRefObject<number>,
  roundStartTimeRef: React.MutableRefObject<number>,
  roundOverridesRef: React.MutableRefObject<SpaceSpiralRoundConfig | null>,
  ballPoolRef: React.MutableRefObject<BallPool>,
  trailCirclePoolRef: React.MutableRefObject<TrailCirclePool>,
  speedIncreaseRateRef: React.MutableRefObject<number>,
  ballGrowthRateRef: React.MutableRefObject<number>,
  containerShrinkRateRef: React.MutableRefObject<number>,
  rhythmicBreakPiecesRef: React.MutableRefObject<BallPiece[]>,
  persistentSpectrumBoostRef: React.MutableRefObject<Float32Array>,
  rhythmicResetTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
  rhythmicSimulationsStateRef: React.MutableRefObject<RhythmicSimulationState[]>,
  simTimeRef: React.MutableRefObject<number>,
  rhythmicEscapeShieldActiveRef: React.MutableRefObject<boolean>,
  rhythmicEscapeShieldBouncesRef: React.MutableRefObject<number>,
  rhythmicEscapeShieldTimeRef: React.MutableRefObject<number>,
  rhythmicEscapeShieldAngleRef: React.MutableRefObject<number>,
  resetWorker: () => void,
  sendBallsToWorker: (balls: Ball[]) => void,
  setBallCount: React.Dispatch<React.SetStateAction<number>>,
  setBounceCount: React.Dispatch<React.SetStateAction<number>>,
  setElapsedTime: React.Dispatch<React.SetStateAction<number>>,
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>,
  lastTimeRef: React.MutableRefObject<number>
) => {
  const getInitialBallState = useCallback((index: number, total: number, config: BallPlacementConfig | null = null, isForPreview: boolean = false, radiusOverride?: number) => {
    const cfg = configRef.current;
    const mode = config ? config.mode : cfg.ballPlacementMode;
    const speed = config ? config.speed : cfg.ballPlacementInitialSpeed;
    const randomDir = config ? config.randomDirection : cfg.ballPlacementRandomDirection;
    const dirAngle = config ? config.directionAngle : cfg.ballPlacementDirectionAngle;
    const containerRadius = radiusOverride || cfg.containerRadius;

    // The simulation center is (0,0). The renderer handles translation to canvas center.
    const centerX = 0;
    const centerY = 0;

    let pos = { x: centerX, y: centerY };
    
    if (mode === 'MANUAL') {
        // posX and posY are in range [-100, 100] where 0 is center
        const px = config ? config.posX : cfg.ballPlacementPosX;
        const py = config ? config.posY : cfg.ballPlacementPosY;
        const limit = containerRadius - cfg.ballRadius - 5;
        pos = { 
            x: centerX + (px / 100) * limit, 
            y: centerY + (py / 100) * limit 
        };
    } else if (mode === 'BORDER') {
        const border = config ? config.border : cfg.ballPlacementBorder;
        const offset = containerRadius - cfg.ballRadius - 5;
        if (border === 'TOP') pos = { x: centerX, y: centerY - offset };
        else if (border === 'BOTTOM') pos = { x: centerX, y: centerY + offset };
        else if (border === 'LEFT') pos = { x: centerX - offset, y: centerY };
        else if (border === 'RIGHT') pos = { x: centerX + offset, y: centerY };
    } else if (mode === 'ANGLE') {
        const angle = (config ? config.placementAngle : cfg.ballPlacementAngle) * Math.PI / 180;
        const dist = (config ? config.distance : cfg.ballPlacementDistance) / 100 * containerRadius;
        pos = { 
            x: centerX + Math.cos(angle) * dist, 
            y: centerY + Math.sin(angle) * dist 
        };
    }

    // Clamp to container
    const dx = pos.x - centerX;
    const dy = pos.y - centerY;
    const dist = Math.sqrt(dx*dx + dy*dy);
    const maxDist = containerRadius - cfg.ballRadius - 5;
    if (dist > maxDist) {
        const scale = maxDist / dist;
        pos.x = centerX + dx * scale;
        pos.y = centerY + dy * scale;
    }

    let vel;
    if (randomDir && !isForPreview) {
        const angle = Math.random() * Math.PI * 2;
        vel = { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed };
    } else {
        const angle = dirAngle * Math.PI / 180;
        vel = { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed };
    }

    return { pos, vel };
  }, [configRef]);

  const initSimulation = useCallback((resetRounds: boolean = false) => {
    const cfg = configRef.current;
    
    // Use a local effective config for round overrides to avoid corrupting the global state
    let effectiveCfg = { ...cfg };

    // Playlist Sequential Reset: Start from the first song if in playlist mode
    if (cfg.mode === 'RHYTHMIC' && cfg.playlistEnabled && cfg.playlist.length > 0) {
        const firstItem = cfg.playlist[0];
        let newInitParams: any = {
            playlistCurrentIndex: 0,
            selectedMelody: firstItem.melodyId
        };

        if (firstItem.presetName && firstItem.presetName !== 'Default') {
            const preset = PRESET_LIBRARY.find(p => p.name === firstItem.presetName);
            if (preset) {
                newInitParams = { ...newInitParams, ...preset.settings };
            }
        }
        
        effectiveCfg = { ...effectiveCfg, ...newInitParams };
        setConfig(prev => ({ ...prev, ...newInitParams }));
    }
    
    ballsRef.current.forEach(b => ballPoolRef.current.release(b));
    ballsRef.current = [];
    resetWorker();

    // Reset Shape to Initial
    if (cfg.initialContainerShape && cfg.containerShape !== cfg.initialContainerShape) {
        // Use a functional update to avoid unnecessary re-renders if the value is already correct
        setConfig(prev => {
            if (prev.containerShape === cfg.initialContainerShape) return prev;
            return { ...prev, containerShape: cfg.initialContainerShape };
        });
    }

    AUDIO.resetIndex();
    AUDIO.resetSongCursor(); 
    CAMERA.reset();
    setBounceCount(0);
    
    trailCirclesRef.current.forEach(c => trailCirclePoolRef.current.release(c));
    trailCirclesRef.current = [];
    
    timeScaleRef.current = cfg.initialSpeed || 1.0;
    isRotationStoppedRef.current = false;
    shakeRef.current = 0;
    counterPopRef.current = 1.0;
    containerRadiusRef.current = cfg.containerRadius;
    rhythmicTimeScaleRef.current = cfg.rhythmicTimeScale;
    rhythmicTimeScaleRateRef.current = cfg.rhythmicTimeScaleRate;
    speedIncreaseRateRef.current = cfg.speedIncreaseRate;
    ballGrowthRateRef.current = cfg.ballGrowthRate;
    containerShrinkRateRef.current = cfg.containerShrinkRate;
    simTimeRef.current = 0;
    morphTimerRef.current = 0;
    morphStateRef.current = { active: false, from: configRef.current.containerShape, to: configRef.current.containerShape, progress: 0 };
    
    const createBall = (overrides: Partial<Ball>): Ball => {
        const b = ballPoolRef.current.get(
            overrides.id || Math.random().toString(),
            overrides.pos || { x: 0, y: 0 },
            overrides.vel || { x: (Math.random()-0.5)*10, y: (Math.random()-0.5)*10 },
            overrides.radius || effectiveCfg.ballRadius,
            overrides.color || getRandomColor(),
            overrides.mass || effectiveCfg.ballMass,
            overrides.style || effectiveCfg.ballStyle,
            overrides.isBox || false
        );
        if (overrides.rotationSpeed !== undefined) b.rotationSpeed = overrides.rotationSpeed;
        if (overrides.shape) b.shape = overrides.shape;
        if (overrides.dimensions) b.dimensions = overrides.dimensions;
        if (overrides.originalPos) b.originalPos = overrides.originalPos;
        if (overrides.angle !== undefined) b.angle = overrides.angle;
        if (overrides.isBox !== undefined) b.isBox = overrides.isBox;
        if (overrides.spawnTime !== undefined) b.spawnTime = overrides.spawnTime;
        return b;
    };

    if (cfg.mode === 'RHYTHMIC') {
        if (cfg.rhythmicMultiSimEnabled) {
            // Initialize multiple simulations
            const sims = cfg.rhythmicSimulations;
            const count = sims.length;
            const spacing = window.innerWidth / (count + 1);
            
            rhythmicSimulationsStateRef.current = sims.map((sim, i) => {
                const simSettings = sim.settings || {};
                // If intro mode is on, start at center (0,0) so the first one is centered immediately
                const simCenter = cfg.rhythmicMultiSimIntroMode ? { x: 0, y: 0 } : (simSettings.rhythmicSimCenter || { x: spacing * (i + 1) - window.innerWidth / 2, y: 0 });
                const simRadius = simSettings.containerRadius || cfg.containerRadius;
                
                const state: RhythmicSimulationState = {
                    id: sim.id,
                    ball: null,
                    containerRadius: simRadius,
                    rhythmicIndex: 0,
                    ballGrowthRate: simSettings.ballGrowthRate ?? cfg.ballGrowthRate,
                    rhythmicTimeScale: simSettings.rhythmicTimeScale ?? cfg.rhythmicTimeScale,
                    rhythmicTimeScaleRate: simSettings.rhythmicTimeScaleRate ?? cfg.rhythmicTimeScaleRate,
                    center: { ...simCenter },
                    targetCenter: { ...simCenter },
                    active: !cfg.rhythmicMultiSimIntroMode,
                    spawnTime: Date.now(),
                    trackIndices: sim.trackIndices,
                    backgroundTrackIndices: sim.backgroundTrackIndices || [],
                    mutedTrackIndices: sim.mutedTrackIndices || [],
                    settings: sim.settings
                };

                if (state.active) {
                    const localIndexRef = { current: 0 };
                    const mergedCfg = { ...effectiveCfg, ...simSettings };
                    state.ball = RhythmicLogic.init(mergedCfg, localIndexRef as any, createBall, simCenter.x, simCenter.y, (idx, tot, c) => getInitialBallState(idx, tot, c, false, mergedCfg.containerRadius))[0];
                    state.rhythmicIndex = localIndexRef.current;
                }

                return state;
            });
            ballsRef.current = rhythmicSimulationsStateRef.current.map(s => s.ball).filter(b => b !== null) as Ball[];
        } else {
            ballsRef.current = RhythmicLogic.init(effectiveCfg, rhythmicIndexRef, createBall, 0, 0, getInitialBallState);
        }
    } else if (cfg.mode === 'ESCAPE') {
        ballsRef.current = EscapeLogic.init(effectiveCfg, 0, 0, escapeRingsRef, initialEscapeRingCountRef, timeRemainingRef, escapeAccelerationBoostRef, createBall, getInitialBallState);
    } else if (cfg.mode === 'RHYTHMIC_ESCAPE') {
        ballsRef.current = RhythmicEscapeLogic.init(effectiveCfg, escapeRingsRef, initialEscapeRingCountRef, rhythmicEscapeShieldActiveRef, rhythmicEscapeShieldBouncesRef, rhythmicEscapeShieldTimeRef, rhythmicEscapeShieldAngleRef, rhythmicIndexRef, createBall, 0, 0);
    } else if (cfg.mode === 'SPACE_SPIRAL') {
        ballsRef.current = SpaceSpiralLogic.init(effectiveCfg, 0, 0, currentRoundRef, roundStartTimeRef, roundOverridesRef, resetRounds, createBall, getInitialBallState);
    } else {
        ballsRef.current = BasicModesLogic.init(effectiveCfg, 0, 0, containerRadiusRef, createBall, getInitialBallState);
    }
    setBallCount(ballsRef.current.length);
    setElapsedTime(0);
    lastTimeRef.current = Date.now();
    collisionEventsRef.current = [];
    blastEventsRef.current = [];
    rhythmicBreakPiecesRef.current = [];
    if (persistentSpectrumBoostRef.current) {
        persistentSpectrumBoostRef.current.fill(0);
    }
    if (rhythmicResetTimeoutRef.current) {
        clearTimeout(rhythmicResetTimeoutRef.current);
        rhythmicResetTimeoutRef.current = null;
    }
    sendBallsToWorker(ballsRef.current);
  }, [configRef, resetWorker, sendBallsToWorker, getInitialBallState, setBallCount, setBounceCount, setElapsedTime, lastTimeRef]);

  const getBallPreviews = useCallback(() => {
    const cfg = configRef.current;
    const previews: { pos: {x:number, y:number}, vel: {x:number, y:number}, radius: number, color: string }[] = [];
    const count = cfg.initialBalls;
    for (let i = 0; i < count; i++) {
        const individualCfg = cfg.ballPlacementIndividualEnabled && cfg.ballPlacementConfigs[i] ? cfg.ballPlacementConfigs[i] : null;
        const { pos, vel } = getInitialBallState(i, count, individualCfg, true);
        previews.push({
            pos,
            vel,
            radius: cfg.ballRadius,
            color: individualCfg?.color || (i === 0 ? cfg.ball1Color : (i === 1 ? cfg.ball2Color : '#ffffff'))
        });
    }
    return previews;
  }, [configRef, getInitialBallState]);

  return { initSimulation, getBallPreviews };
};
